<template>
  <v-row class="border-sm border-solid border-primary">
    <v-col cols="12" sm="6" md="8" :class="{ 'bg-red-lighten-4': !release.release }">
      <v-text-field
        v-model="release.title"
        :rules="[rules.required]"
        label="Release Name"
        @update:model-value="handleUploadComplted"></v-text-field>
    </v-col>
    <v-col cols="12" sm="6" md="4" class="d-flex justify-end">
      <v-btn icon variant="text" small color="primary" @click="openSig">
        <v-icon icon="mdi-draw-pen"></v-icon>
      </v-btn>
      <v-btn icon variant="text" small color="primary" @click="isUploadOpen = !isUploadOpen">
        <v-icon icon="mdi-upload"></v-icon>
      </v-btn>

      <v-btn
        :disabled="!release.release"
        icon
        variant="text"
        small
        color="primary"
        @click="downloadFile(release?.release?.url)">
        <v-icon icon="mdi-download"></v-icon>
      </v-btn>
      <v-btn icon variant="text" small color="primary" @click="toggleDelete">
        <v-icon icon="mdi-trash-can-outline"></v-icon>
      </v-btn>
    </v-col>
  </v-row>
  <v-dialog v-model="isUploadOpen">
    <AssetUploadComponent
      type="pdf"
      :on-upload="handleFileUpload"
      @upload-canceled="isUploadOpen = false"
      @upload-completed="handleUploadComplted"></AssetUploadComponent>
  </v-dialog>
  <ConfirmDeleteComponent
    item="Release"
    :is-open="isDeleteOpen"
    :description="release.title || ''"
    @action-canceled="toggleDelete"
    @action-confirmed="removeRelease" />
  <v-dialog v-model="isSignatureOpen">
    <ReleaseSignComponent
      :form="form"
      :formFields="fields"
      :input-fields="inputFields"
      :isModal="true"
      @sig-canceled="isSignatureOpen = false"
      @sig-completed="handleSigUpload"></ReleaseSignComponent>
  </v-dialog>
</template>
<script setup lang="ts">
import {
  FormTemplateDTO,
  IAssetDTO,
  IFormTemplateDTO,
  IInputFieldsDTO,
  IReleaseDTO,
  IReleaseTypeDTO,
  ISubmissionDTO,
  ReleaseDTO,
} from '@/dtos';
import { onMounted, ref, watch } from 'vue';
import AssetUploadComponent from '../ui/AssetUploadComponent.vue';
import { createProgressHandler, uploadSubmissionRelease } from '@/controllers';
import {
  DEFAULT_LOCATION_FORM,
  DEFAULT_MUSIC_FORM,
  useFormTemplateStore,
  useReleaseStore,
  useTeamStore,
} from '@/stores';
import ConfirmDeleteComponent from '../ui/ConfirmDeleteComponent.vue';
import { rules, downloadFile, IBaseFormFields, currentDate, browser, userIP } from '@/helpers';
import ReleaseSignComponent from './ReleaseSignComponent.vue';

interface Props {
  submission: ISubmissionDTO;
  releaseType: IReleaseTypeDTO;
  release: IReleaseDTO;
}
const props = defineProps<Props>();
const release = ref<IReleaseDTO>(new ReleaseDTO());
const isUploadOpen = ref<boolean>(false);
const isDeleteOpen = ref<boolean>(false);
const isSignatureOpen = ref<boolean>(false);
const releaseStore = useReleaseStore();
const form = ref<IFormTemplateDTO>(new FormTemplateDTO());
const teamStore = useTeamStore();
const formStore = useFormTemplateStore();
const formTemplateId = ref<string>('');
const { data } = formStore.getFormTemplateById(formTemplateId);
const inputFields = ref<IInputFieldsDTO[]>([]);
watch(
  () => props.release,
  (newValue?: IReleaseDTO) => {
    if (newValue) {
      release.value = new ReleaseDTO(newValue);
    } else {
      release.value = new ReleaseDTO();
    }
  },
  { immediate: true },
);

const fields = ref<IBaseFormFields>({
  date: currentDate.value,
  ipAddress: '',
  browser: browser.value,
  teamName: '',
});
const handleFileUpload = async (
  file: File,
  updateProgress: (progress: number) => void,
): Promise<IAssetDTO> => {
  const response = await uploadSubmissionRelease(
    props.submission.id,
    props.releaseType.id,
    file,
    updateProgress,
  );
  release.value.release = response;
  return response;
};

const handleUploadComplted = async (): Promise<void> => {
  release.value.submission = props.submission;
  release.value.releaseType = props.releaseType;
  await releaseStore.save.mutateAsync(release.value);
  isUploadOpen.value = false;
};

const handleSigUpload = async (file: File) => {
  const uploadProgress = ref<number>(0);
  const uploadCompleted = ref<boolean>(false);
  const progress = createProgressHandler(uploadProgress, uploadCompleted);
  await handleFileUpload(file, progress);
  await handleUploadComplted();
  isSignatureOpen.value = false;
};

const toggleDelete = () => {
  isDeleteOpen.value = !isDeleteOpen.value;
};

const removeRelease = async () => {
  release.value.submission = props.submission;
  if (release.value.id) {
    await releaseStore.remove.mutateAsync(release.value);
  } else {
    await releaseStore.invalidate();
  }
  toggleDelete();
};

const openSig = () => {
  inputFields.value = [];
  if (props.releaseType.release_name == 'Music') {
    formTemplateId.value = DEFAULT_MUSIC_FORM.value;
    inputFields.value = [{ title: 'Artist' }, { title: 'Song Title' }];
  } else if (props.releaseType.release_name == 'Location') {
    inputFields.value = [{ title: 'Location Name' }, { title: 'Location Address' }];
    formTemplateId.value = DEFAULT_LOCATION_FORM.value;
  }

  isSignatureOpen.value = true;
};

watch(
  () => data.value,
  (newValue: IFormTemplateDTO | undefined) => {
    if (newValue) {
      form.value = new FormTemplateDTO(newValue);
    } else {
      form.value = new FormTemplateDTO();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  fields.value.ipAddress = (await userIP.value) || '';
  fields.value.teamName = teamStore.myTeam?.name;
});
</script>
